import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import { KioskFaceCapture } from './KioskFaceCapture';
import {
  registerMobileAttendanceCapture,
  type MobileCaptureRequest,
} from '../services/mobileAttendanceCapture';
import { deleteTemporaryImage } from '../utils/temporaryImage';

/** Reuses the kiosk's automatic front-camera scanner inside the current app. */
export function MobileAttendanceFaceVerification() {
  const { status, user } = useAuth();
  const [request, setRequest] = useState<MobileCaptureRequest | null>(null);
  const [verifying, setVerifying] = useState(false);
  const current = useRef<MobileCaptureRequest | null>(null);
  const captured = useRef(false);
  useEffect(() => {
    if (status !== 'authenticated') return;
    const unregister = registerMobileAttendanceCapture(next => {
      current.current = next;
      captured.current = false;
      setVerifying(false);
      setRequest(next);
    });
    return unregister;
  }, [status, user?.userId]);
  if (!request) return null;
  const cancel = () =>
    request.failed(
      'Face verification was cancelled. No attendance action was recorded.',
      'CAPTURE_CANCELED',
    );
  return (
    <Modal
      visible
      animationType="fade"
      onRequestClose={() => {
        if (!verifying) cancel();
      }}
    >
      <View style={styles.fill}>
        {verifying ? (
          <View style={styles.progress}>
            <ActivityIndicator size="large" color="white" />
            <Text style={styles.text}>Verifying your face…</Text>
          </View>
        ) : (
          <>
            <KioskFaceCapture
              heading="Employee attendance"
              onCaptured={image => {
                if (current.current !== request || captured.current) {
                  void deleteTemporaryImage(image.uri);
                  return;
                }
                captured.current = true;
                setVerifying(true);
                request.captured(image);
              }}
              onError={message => request.failed(message)}
              onTimeout={() =>
                request.failed(
                  'No face was detected. Please try again.',
                  'FACE_NOT_DETECTED',
                )
              }
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Cancel face verification"
              onPress={cancel}
              style={styles.cancel}
            >
              <Text style={styles.text}>Cancel</Text>
            </Pressable>
          </>
        )}
      </View>
    </Modal>
  );
}
const styles = StyleSheet.create({
  fill: { flex: 1, backgroundColor: '#0B1220' },
  progress: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  text: { color: 'white', fontSize: 16 },
  cancel: { alignSelf: 'center', padding: 16, marginBottom: 24 },
});
